import React from 'react';
import { Button, Card, CardActions, CardContent } from '@material-ui/core';
import LinesEllipsis from 'react-lines-ellipsis';
import { formatInTimeZone } from 'date-fns-tz';
import { convertTokens } from '@date-fns/upgrade/v2';

import { black, purple, white } from 'utils/color-styles';
import OrganizationChip from 'components/OrganizationChip';
import { ClickableUrl } from 'utils/urls';

import { Opportunity } from '../types';
import { ProviderLogo } from '../ProviderLogo';

import { OpportunityCardImage } from './OpportunityCardImage';
import { ImportOpportunityButton } from './ImportOpportunityButton';

export const OpportunityCard = ({
  opportunity,
  setOpportunityPreview,
}: {
  opportunity: Opportunity;
  setOpportunityPreview: (opportunity: Opportunity) => void;
}) => {
  const timeZone =
    opportunity.timeZone && !opportunity.virtual
      ? opportunity.timeZone
      : Intl.DateTimeFormat()?.resolvedOptions()?.timeZone;

  const topLeft = (
    <div style={{ position: 'absolute', top: 16, left: 16 }}>
      {opportunity.startingAt ? (
        <div
          style={{
            flexDirection: 'row',
          }}
        >
          <div style={{ color: 'white', fontWeight: 500 }}>
            {formatInTimeZone(opportunity.startingAt, timeZone, convertTokens('DD MMMM'))}
          </div>
        </div>
      ) : null}
      {(opportunity.startingAt || opportunity.endingAt) && (
        <div
          style={{
            fontSize: 12,
            letterSpacing: -0.3,
            color: 'white',
          }}
        >
          {opportunity.startingAt &&
            formatInTimeZone(opportunity.startingAt, timeZone, convertTokens('hh:mmA'))}
          {opportunity.endingAt && opportunity.endingAt !== opportunity.startingAt && (
            <> – {formatInTimeZone(opportunity.endingAt, timeZone, convertTokens('hh:mmA'))}</>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <Card
        style={{
          position: 'relative',
          borderRadius: 15,
          borderWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ position: 'relative' }}>
          <OpportunityCardImage opportunity={opportunity} />
          {topLeft}
          <div
            style={{
              borderRadius: 4,
              padding: 5,
              backgroundColor: white,
              fontSize: 12,
              color: black,
              position: 'absolute',
              bottom: 16,
              left: 16,
            }}
          >
            {opportunity.virtual ? 'Virtual' : 'In-person'}
          </div>
          <div
            style={{
              borderRadius: 4,
              padding: 5,
              backgroundColor: purple,
              fontSize: 12,
              color: white,
              position: 'absolute',
              bottom: 16,
              right: 16,
            }}
          >
            {opportunity.type}
          </div>
        </div>
        <CardContent style={{ padding: '16px 16px 8px 16px', flexGrow: 1 }}>
          <LinesEllipsis
            text={opportunity.name}
            maxLine={2}
            component="h4"
            style={{
              height: 48,
              fontFamily: 'Graphik',
              margin: '0 0 14px 0',
              fontSize: 18,
              fontWeight: 500,
            }}
          />

          {opportunity.nonprofit && (
            <OrganizationChip
              size="small"
              organization={{
                name: opportunity.nonprofit.name,
                logo: opportunity.nonprofit.pictures?.[0],
              }}
            />
          )}
        </CardContent>
        <CardActions style={{ padding: '0 16px', marginBottom: 8, alignItems: 'flex-end' }}>
          {opportunity.externalLink ? (
            <ClickableUrl href={opportunity.externalLink} rel="noopener noreferrer" target="_blank">
              <ProviderLogo provider={opportunity.provider} />
            </ClickableUrl>
          ) : (
            <ProviderLogo provider={opportunity.provider} />
          )}
          <Button
            color="primary"
            variant="outlined"
            onClick={() => {
              setOpportunityPreview(opportunity);
            }}
            style={{ margin: '0 0 8px 8px' }}
          >
            Details
          </Button>
          <ImportOpportunityButton opportunity={opportunity} />
        </CardActions>
      </Card>
    </>
  );
};
